import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ImagePlus, RefreshCw, Loader2, User, UserX, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { runImageJob } from "@/lib/imageJobs";
import { absoluteImageUrl } from "@/components/ImageUploader";

const paragraphsOf = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return Array.from(doc.body.querySelectorAll("p, h2, h3, blockquote, li"))
    .map((el) => (el.textContent || "").replace(/\s+/g, " ").trim())
    .filter((t) => t.length >= 40);
};

const PRESENCE = [
  { key: "auto", label: "Lasă AI-ul", Icon: Wand2 },
  { key: "yes", label: "Cu mine", Icon: User },
  { key: "no", label: "Fără mine", Icon: UserX },
];

const CoverBlock = ({ data, busy, onRegenerate }) => {
  const generating = data.cover_status === "generating" || busy;
  const [instruction, setInstruction] = useState("");
  return (
    <div className="flex items-start gap-3" data-testid="assistant-cover-block">
      <div className="h-20 w-36 shrink-0 overflow-hidden rounded-lg bg-[#1A120D] ring-1 ring-[color-mix(in_srgb,var(--milk)_14%,transparent)]">
        {data.cover_image ? (
          <img src={absoluteImageUrl(data.cover_image)} alt={data.cover_alt || ""} className="h-full w-full object-cover" data-testid="assistant-cover-img" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[#F7F1E8]/50">
            {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--teal-dark)]">Coperta</p>
        <p className="mt-1 text-xs text-[#F7F1E8]/75" data-testid="assistant-cover-status">
          {generating ? "Se generează din întregul articol, în stilul blogului…" : data.cover_status === "failed" ? "Generarea a eșuat — încearcă din nou." : data.cover_image ? "Gata. O poți regenera dacă vrei alta." : "Fără copertă încă."}
        </p>
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={2}
          maxLength={1500}
          placeholder="Opțional: spune-i AI-ului ce să arate pe copertă (ex.: eu, în fața oglinzii, cu telefonul întors cu fața în jos). Dacă lași gol, alege el din articol."
          data-testid="assistant-cover-instruction"
          className="mt-2 w-full rounded-lg border border-[color-mix(in_srgb,var(--milk)_14%,transparent)] bg-[#1A120D] px-3 py-2 text-xs text-[#F7F1E8] outline-none focus:border-[var(--accent)]"
        />
        <button
          type="button"
          onClick={() => onRegenerate(instruction)}
          disabled={generating}
          data-testid="assistant-cover-regenerate-btn"
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--accent)] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-[var(--accent)] transition-colors hover:bg-[var(--accent)] hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${generating ? "animate-spin" : ""}`} /> {data.cover_image ? "Regenerează din articol" : "Generează din articol"}
        </button>
      </div>
    </div>
  );
};

const ParagraphRow = ({ index, text, slot, setSlot, presence, setPresence, busy, onGenerate }) => (
  <div className="rounded-lg bg-[#1A120D]/70 px-3 py-2" data-testid={`assistant-paragraph-${index}`}>
    <p className="text-xs leading-relaxed text-[#F7F1E8]/80">{text.length > 220 ? `${text.slice(0, 220)}…` : text}</p>
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <select
        value={slot}
        onChange={(e) => setSlot(Number(e.target.value))}
        data-testid={`assistant-paragraph-slot-${index}`}
        className="rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-transparent px-2 py-1 text-[11px] text-[#F7F1E8]"
      >
        {[1, 2, 3].map((s) => <option key={s} value={s} className="bg-[#1A120D]">Poza {s} din text</option>)}
      </select>
      <div className="flex overflow-hidden rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
        {PRESENCE.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setPresence(key)}
            data-testid={`assistant-paragraph-presence-${index}-${key}`}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.12em] ${presence === key ? "bg-[var(--accent)] text-white" : "text-[#F7F1E8]/70"}`}
          >
            <Icon className="h-3 w-3" /> {label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={busy}
        data-testid={`assistant-paragraph-generate-${index}`}
        className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-white disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />} Imagine din paragraf
      </button>
    </div>
  </div>
);

const SlotRow = ({ slot, image, busy, onGenerate }) => {
  const [instruction, setInstruction] = useState("");
  const [presence, setPresence] = useState("auto");
  return (
    <div className="rounded-lg bg-[#1A120D]/70 px-3 py-2" data-testid={`assistant-slot-${slot}`}>
      <div className="flex items-start gap-3">
        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-[#0D0907] ring-1 ring-[color-mix(in_srgb,var(--milk)_14%,transparent)]">
          {image?.url ? <img src={absoluteImageUrl(image.url)} alt={image.alt || ""} className="h-full w-full object-cover" data-testid={`assistant-slot-img-${slot}`} /> : <div className="flex h-full items-center justify-center text-[10px] text-[#F7F1E8]/40">Poza {slot}</div>}
        </div>
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={2}
          maxLength={1500}
          placeholder={`Poza ${slot} din text — descrie scena (ex.: eu la masă, cu o scrisoare nedeschisă; sau: o ușă întredeschisă, fără mine)`}
          data-testid={`assistant-slot-instruction-${slot}`}
          className="min-w-0 flex-1 rounded-lg border border-[color-mix(in_srgb,var(--milk)_14%,transparent)] bg-[#0D0907] px-3 py-2 text-xs text-[#F7F1E8] outline-none focus:border-[var(--accent)]"
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
          {PRESENCE.map(({ key, label, Icon }) => (
            <button key={key} type="button" onClick={() => setPresence(key)} data-testid={`assistant-slot-presence-${slot}-${key}`} className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.12em] ${presence === key ? "bg-[var(--accent)] text-white" : "text-[#F7F1E8]/70"}`}>
              <Icon className="h-3 w-3" /> {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onGenerate(instruction, presence)}
          disabled={busy || instruction.trim().length < 10}
          data-testid={`assistant-slot-generate-${slot}`}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} {image?.url ? "Refă poza" : "Creează poza"}
        </button>
      </div>
    </div>
  );
};

export const AssistantImagesPanel = ({ postId }) => {
  const [data, setData] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [busyIdx, setBusyIdx] = useState(null);
  const [slots, setSlots] = useState({});
  const [presence, setPresence] = useState({});
  const [open, setOpen] = useState(false);
  const [busySlot, setBusySlot] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/posts/${postId}/images`);
      setData(d);
    } catch {
      /* panel stays hidden */
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.cover_status !== "generating") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.cover_status, load]);

  const paragraphs = useMemo(() => paragraphsOf(data?.content), [data?.content]);
  if (!data) return null;

  const regenerateCover = async (instruction = "") => {
    setCoverBusy(true);
    try {
      await runImageJob("post-cover", { instruction }, postId);
      toast.success("Coperta a fost generată din articol.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut genera coperta."));
    } finally {
      setCoverBusy(false);
    }
  };

  const generateSlot = async (slot, instruction, alexia) => {
    setBusySlot(slot);
    try {
      const r = await runImageJob("post-paragraph", { instruction, slot, alexia }, postId);
      setData((d) => ({ ...d, body_images: r.body_images }));
      toast.success(`Poza ${slot} a fost pusă în articol.`);
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut genera imaginea."));
    } finally {
      setBusySlot(null);
    }
  };

  const generateParagraph = async (i) => {
    setBusyIdx(i);
    try {
      const r = await runImageJob("post-paragraph", {
        text: paragraphs[i], slot: slots[i] || Math.min(3, (data.body_images?.length || 0) + 1), alexia: presence[i] || "auto",
      }, postId);
      setData((d) => ({ ...d, body_images: r.body_images }));
      toast.success("Imaginea a fost pusă în articol.");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut genera imaginea."));
    } finally {
      setBusyIdx(null);
    }
  };

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-[color-mix(in_srgb,var(--milk)_12%,transparent)] bg-[#2B1B13] px-4 py-3" data-testid="assistant-images-panel">
      <CoverBlock data={data} busy={coverBusy} onRegenerate={regenerateCover} />
      <div className="space-y-2" data-testid="assistant-body-images">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--teal-dark)]">Pozele din corpul textului — spune-i AI-ului ce să arate</p>
        {[1, 2, 3].map((slot) => (
          <SlotRow key={slot} slot={slot} image={data.body_images?.[slot - 1]} busy={busySlot === slot} onGenerate={(ins, pres) => generateSlot(slot, ins, pres)} />
        ))}
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-paragraphs-toggle"
        className="text-[11px] font-medium uppercase tracking-[0.2em] text-[var(--accent-dark)] underline underline-offset-4"
      >
        {open ? "Ascunde paragrafele" : `Imagini din paragrafe (${paragraphs.length})`}
      </button>
      {open && (
        <div className="space-y-2">
          {paragraphs.map((t, i) => (
            <ParagraphRow
              key={i}
              index={i}
              text={t}
              slot={slots[i] || Math.min(3, (data.body_images?.length || 0) + 1)}
              setSlot={(s) => setSlots((p) => ({ ...p, [i]: s }))}
              presence={presence[i] || "auto"}
              setPresence={(k) => setPresence((p) => ({ ...p, [i]: k }))}
              busy={busyIdx === i}
              onGenerate={() => generateParagraph(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AssistantImagesPanel;
